import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Container,
  Grid,
  Card,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { saveQuestionResultsToNeon, getSessionResults } from '../services/neonDb';
import Stratified4ItemRanker from './StratifiedMergeRanker';
import MetricsDisplay from './MetricsDisplay';

// Item descriptions (keeping the same as existing implementation)
const ITEM_DESCRIPTIONS = {
  // REALISTE
  "Travailler avec son corps": "Utiliser son corps comme instrument de travail, utiliser sa force physique, faire de la danse",
  "Utiliser des outils pour fabriquer, réparer, entretenir": "Fabriquer, réparer et entretenir des objets à l'aide d'outils",
  "Améliorer un dispositif, des machines, des appareils": "Optimiser le fonctionnement de machines ou d'appareils, innover techniquement",
  "Travailler avec son habileté manuelle": "Utiliser sa dextérité manuelle pour manipuler des objets de manière précise",
  
  // INVESTIGATEUR
  "Réfléchir à des problèmes, à des situations complexes": "Analyser des situations, des phénomènes, des concepts; formuler des hypothèses; faire des synthèses",
  "Rechercher et recueillir de l'information": "Rechercher et collecter de l'information par divers moyens tels que la lecture, l'interview, l'observation",
  "Résoudre des problèmes avec des nombres": "Faire des calculs, des statistiques, utiliser les mathématiques",
  "Manier les mots, rédiger": "Rédiger des textes, rapports, documents, articles de manière précise",
  
  // SOCIAL
  "Accompagner, aider autrui": "Conseiller, servir, éduquer, évaluer, soigner, défendre des personnes",
  "Travailler en équipe": "Participer et collaborer à un projet commun, travailler en réseau, coopérer",
  "Communiquer, informer, échanger avec des personnes": "Établir un contact avec les autres, échanger des informations, dialoguer",
  "Parler devant un grand nombre de personnes": "S'exprimer en public, être en contact avec de nombreux clients, divertir une assistance",
  
  // ENTREPRENEUR
  "Exercer son leadership": "Motiver, encourager son équipe de travail; diriger d'autres personnes; influencer son entourage",
  "Entreprendre et réaliser un projet": "Élaborer un projet; prendre des décisions; organiser les opérations en vue de sa concrétisation",
  "Présentez avec conviction une idée, un produit ou un projet": "Faire connaître et mettre en valeur une idée, un produit, un projet; convaincre ses interlocuteurs",
  "Travailler avec une grande autonomie et peu de routine": "Travailler en disposant d'une grande liberté; se confronter à des situations inédites; improviser",
  
  // ARTISTIQUE
  "Transformer, adapter": "Modifier des éléments existants pour créer quelque chose de nouveau",
  "Exprimer son sens artistique": "Dessiner; communiquer au moyen d'images; faire de la musique, du théâtre",
  "Créer, Inventer, Innover": "Trouver des solutions inédites à des problèmes; concevoir de nouveaux produits; créer des formes artistiques originales",
  "Exprimer sa sensibilité": "Communiquer ses émotions, ses sentiments à travers diverses formes d'expression",
  
  // CONVENTIONNEL
  "Travailler avec les technologies": "Utiliser des outils technologiques tels que: ordinateur, matériel électronique, appareils audiovisuels",
  "Assumer des tâches administratives": "Tenir à jour, assurer le suivi des dossiers; classer, archiver des données ou des informations",
  "Suivre des règles, des consignes": "Exécuter des tâches selon des règles ou procédures prescrites",
  "Superviser l'exécution des tâches": "Évaluer l'état d'avancement de la tâche; vérifier la qualité de son exécution"
};

// The main questions to be asked
const QUESTIONS = [
  {
    id: 1,
    text: "Classez ces activités selon le temps que vous y consacrez actuellement (de celle qui occupe le plus de temps à celle qui en occupe le moins)",
    key: "time_spent"
  },
  {
    id: 2,
    text: "Classez ces activités selon le temps que vous souhaiteriez y consacrer (de celle à laquelle vous aimeriez consacrer le plus de temps à celle qui vous intéresse le moins)",
    key: "time_desired"
  },
  {
    id: 3,
    text: "Classez ces activités selon vos compétences (de celle où vous êtes le plus compétent à celle où vous êtes le moins compétent)",
    key: "competency"
  }
];

// Styled components for the drag-and-drop interface
const ItemCard = styled(Card)(({ theme, isdragging }) => ({
  marginBottom: theme.spacing(2),
  cursor: isdragging === 'true' ? 'grabbing' : 'grab',
  transition: 'all 0.2s ease',
  boxShadow: isdragging === 'true' 
    ? '0 10px 15px rgba(0, 0, 0, 0.2)' 
    : '0 2px 5px rgba(0, 0, 0, 0.1)',
  transform: isdragging === 'true' ? 'scale(1.02)' : 'scale(1)',
  backgroundColor: isdragging === 'true' ? '#f0f7ff' : 'white',
  '&:hover': {
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
  },
  position: 'relative',
  zIndex: isdragging === 'true' ? 1000 : 1,
  touchAction: 'none',
  padding: theme.spacing(1),
  border: isdragging === 'true' ? `1px solid ${theme.palette.primary.main}` : 'none',
}));

// Flip card for item description
const FlipCard = styled('div')(({ theme }) => ({
  perspective: '1000px',
  width: '100%',
  height: '100%',
  minHeight: '60px',
  cursor: 'pointer',
  '& *': {
    userSelect: 'none',
  },
}));

const FlipCardInner = styled('div')(({ isFlipped }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '60px',
  transition: 'transform 0.6s',
  transformStyle: 'preserve-3d',
  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
}));

const FlipCardFace = styled(Box)(({ isBack }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  minHeight: '60px',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px',
  transform: isBack ? 'rotateY(180deg)' : 'rotateY(0)',
}));

const DragList = styled('div')({
  padding: '8px',
  width: '100%',
  touchAction: 'none',
});

const DragHandle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  touchAction: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
  width: '100%',
  height: '100%',
});

// Individual item component with flip functionality
const RankableItem = ({ item, index, isDragging, dragHandleProps }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  
  const handleFlip = (e) => {
    // Prevent flip when dragging
    if (!isDragging) {
      e.stopPropagation(); // Prevent event from bubbling up to draggable
      setIsFlipped(!isFlipped);
    }
  };
  
  return (
    <ItemCard isdragging={isDragging.toString()}>
      <Grid container alignItems="center">
        <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            {index + 1}
          </Typography>
        </Grid>
        
        <Grid item xs={10}>
          <Box onClick={handleFlip} sx={{ width: '100%', cursor: 'pointer' }}>
            <FlipCard>
              <FlipCardInner isFlipped={isFlipped}>
                <FlipCardFace>
                  <Typography variant="body1">
                    {item}
                  </Typography>
                </FlipCardFace>
                <FlipCardFace isBack>
                  <Typography variant="body2" color="text.secondary">
                    {ITEM_DESCRIPTIONS[item] || "Description non disponible"}
                  </Typography>
                </FlipCardFace>
              </FlipCardInner>
            </FlipCard>
          </Box>
        </Grid>
        
        <Grid item xs={1}>
          <DragHandle {...dragHandleProps}>
            <DragIndicatorIcon color="primary" fontSize="medium" />
          </DragHandle>
        </Grid>
      </Grid>
    </ItemCard>
  );
};

// Main ranker component
const FourItemRanker = ({ 
  items = [], 
  onComplete = null, 
  sessionId = null,
  initialSession = null 
}) => {
  // State management
  const [ranker, setRanker] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [currentRanking, setCurrentRanking] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showNextButton, setShowNextButton] = useState(false);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [allResults, setAllResults] = useState({
    sessionId: sessionId,
    startTime: new Date().toISOString(),
    questions: []
  });
  const [metrics, setMetrics] = useState({
    coherence: 1,
    nuance: 0
  });
  const [previousState, setPreviousState] = useState(null);
  const [canGoBack, setCanGoBack] = useState(false);
  
  // Initialize a new question
  const initializeQuestion = useCallback(() => {
    const newRanker = new Stratified4ItemRanker(items);
    setRanker(newRanker);
    
    // Get the first group to rank
    const firstGroup = newRanker.initializeRanking();
    setCurrentGroup(firstGroup);
    setCurrentRanking([...firstGroup]); // Initial order
  }, [items]);
  
  // Submit the current ranking
  const handleSubmitRanking = useCallback(async () => {
    try {
      // Save current state for "go back" functionality
      setPreviousState({
        rankerState: ranker.getState(),
        currentGroup,
        currentRanking
      });
      setCanGoBack(true);
      
      console.log("Submitting ranking:", currentRanking);
      console.log("Current phase:", ranker.mergePhase ? "Merge phase" : "Initial phase");
      
      // Submit to ranker
      const nextGroup = ranker.addRankedGroup(currentGroup, currentRanking);
      console.log("Next group:", nextGroup);
      
      // Update metrics
      setMetrics({
        coherence: ranker.calculateCoherence(),
        nuance: ranker.calculateNuance()
      });
      
      if (nextGroup) {
        console.log("Moving to next group:", nextGroup);
        // We have more groups to rank
        setCurrentGroup(nextGroup);
        setCurrentRanking([...nextGroup]);
      } else {
        console.log("Finished all groups for this question");
        // We're done with this question
        const rankings = ranker.computeRankings();
        const currentQuestion = QUESTIONS[currentQuestionIndex];
        
        const questionResult = {
          questionId: currentQuestion.id,
          questionKey: currentQuestion.key,
          questionText: currentQuestion.text,
          rankings: rankings.map(rank => ({
            ...rank,
            item: rank.item,
            score: parseFloat(rank.score.toFixed(3)),
            category: rank.category,
            rank: rank.rank
          })),
          completionTime: new Date().toISOString(),
          totalComparisons: ranker.comparisons.length,
          metrics: {
            coherence: ranker.calculateCoherence(),
            nuance: ranker.calculateNuance()
          }
        };
        
        // Save results to database - but don't block on errors
        try {
          await saveQuestionResultsToNeon(
            questionResult,
            sessionId,
            currentQuestion.key
          );
          console.log(`Successfully saved question ${currentQuestion.key}`);
        } catch (err) {
          console.error(`Failed to save question ${currentQuestion.key}:`, err);
          // Show error but don't interrupt flow
          setError(`Warning: Failed to save results to database (${err.message}). Your progress will continue, but results may not be saved.`);
        }
        
        // Continue with the process regardless of database saving errors
        setAllResults(prev => ({
          ...prev,
          questions: [...prev.questions, questionResult]
        }));
        
        if (currentQuestionIndex === QUESTIONS.length - 1) {
          // We're done with all questions
          if (onComplete) {
            onComplete(allResults);
          }
        } else {
          // Show next question button
          setShowNextButton(true);
        }
      }
    } catch (err) {
      console.error('Error in handleSubmitRanking:', err);
      setError(`Failed to save ranking: ${err.message || "Unknown error"}. Please try again.`);
    }
  }, [currentGroup, currentRanking, ranker, currentQuestionIndex, sessionId, onComplete, allResults]);
  
  // Handle moving to next question
  const handleNextQuestion = useCallback(() => {
    setShowNextButton(false);
    setCurrentQuestionIndex(prev => prev + 1);
    setPreviousState(null);
    setCanGoBack(false);
    initializeQuestion();
  }, [initializeQuestion]);
  
  // Handle drag end - update the ranking
  const handleDragEnd = useCallback((result) => {
    // Log the drag result for debugging
    console.log('Drag end result:', result);
    
    // Dropped outside the list
    if (!result.destination) {
      console.log('Dropped outside the list');
      return;
    }
    
    // Don't do anything if dropped in place
    if (result.destination.index === result.source.index) {
      console.log('Dropped in the same place');
      return;
    }
    
    // Reorder
    const newRanking = Array.from(currentRanking);
    const [movedItem] = newRanking.splice(result.source.index, 1);
    newRanking.splice(result.destination.index, 0, movedItem);
    
    console.log('New ranking order:', newRanking);
    setCurrentRanking(newRanking);
  }, [currentRanking]);

  // Add a helper function for drag start event
  const handleDragStart = useCallback((start) => {
    console.log('Drag started:', start);
    // You can add additional logic here if needed
  }, []);
  
  // Go back to previous state
  const handleGoBack = useCallback(() => {
    if (!previousState) return;
    
    ranker.setState(previousState.rankerState);
    setCurrentGroup(previousState.currentGroup);
    setCurrentRanking(previousState.currentRanking);
    setPreviousState(null);
    setCanGoBack(false);
  }, [previousState, ranker]);
  
  // Calculate progress percentage
  const calculateProgress = useCallback(() => {
    if (!ranker) return 0;
    
    // Get the total number of items
    const totalItems = items.length; // From config.js
    
    // Calculate the total number of comparisons needed for all questions
    const totalComparisonsPerQuestion = totalItems * 2;
    const totalComparisons = totalComparisonsPerQuestion * QUESTIONS.length;
    
    // Calculate completed comparisons
    const completedQuestionsComparisons = currentQuestionIndex * totalComparisonsPerQuestion;
    
    // If we're showing the next button, we've completed this question
    // so count all comparisons for this question as completed
    const currentQuestionCompletedComparisons = showNextButton 
      ? totalComparisonsPerQuestion 
      : ranker.comparisons.length;
      
    const totalCompletedComparisons = completedQuestionsComparisons + currentQuestionCompletedComparisons;
    
    // Calculate overall progress percentage
    return Math.round((totalCompletedComparisons / totalComparisons) * 100);
  }, [ranker, currentQuestionIndex, showNextButton]);
  
  // Initialize on mount
  useEffect(() => {
    const restoreSession = async () => {
      if (initialSession && sessionId) {
        try {
          const results = await getSessionResults(sessionId);
          if (results.length > 0) {
            const savedQuestions = results.filter(r => r.type !== 'final');
            setCurrentQuestionIndex(savedQuestions.length);
            setAllResults(prev => ({
              ...prev,
              questions: savedQuestions.map(q => {
                if (typeof q.ranking_data === 'string') {
                  return JSON.parse(q.ranking_data);
                }
                return q.ranking_data;
              })
            }));
          }
        } catch (error) {
          console.error('Error restoring session:', error);
          setError('Failed to restore session');
        }
      }
      initializeQuestion();
      setInitializing(false);
    };

    restoreSession();
  }, [initialSession, sessionId, initializeQuestion]);
  
  if (initializing) {
    return <div>Loading session...</div>;
  }
  
  return (
    <Container maxWidth="md">
      <MetricsDisplay metrics={metrics} />
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
          {QUESTIONS[currentQuestionIndex].text}
        </Typography>
        
        {/* Progress Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Question {currentQuestionIndex + 1}/{QUESTIONS.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {calculateProgress()}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={calculateProgress()} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        
        {/* Ranking Section - with Drag and Drop */}
        {currentGroup && !showNextButton && (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Classez ces activités par ordre de préférence
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Glissez et déposez les activités en utilisant l'icône ⠿ pour les classer de la plus importante (1) à la moins importante (4). Cliquez sur une activité pour voir sa description.
            </Typography>
            
            <DragDropContext 
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
            >
              <Droppable droppableId="droppable-ranking-list">
                {(provided, snapshot) => (
                  <DragList
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      background: snapshot.isDraggingOver ? '#f9fafc' : 'transparent',
                      padding: '8px',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {currentRanking.map((item, index) => (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              userSelect: 'none',
                            }}
                          >
                            <RankableItem 
                              item={item}
                              index={index}
                              isDragging={snapshot.isDragging}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </DragList>
                )}
              </Droppable>
            </DragDropContext>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                disabled={!canGoBack}
              >
                Retour
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitRanking}
                size="large"
              >
                Valider ce classement
              </Button>
            </Box>
          </Paper>
        )}
        
        {/* Next Question Section */}
        {showNextButton && (
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" gutterBottom color="primary">
              Question {currentQuestionIndex + 1} terminée !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Résultats enregistrés avec succès.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleNextQuestion}
              sx={{ minWidth: 200 }}
            >
              {currentQuestionIndex < QUESTIONS.length - 1 
                ? "Question suivante" 
                : "Terminer le test"}
            </Button>
          </Paper>
        )}
        
        {/* Error Display */}
        {error && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mt: 2, 
              bgcolor: 'error.light',
              color: 'error.dark',
              borderRadius: 2
            }}
          >
            <Typography>{error}</Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default FourItemRanker; 